import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';

const ENDPOINT_URL = '/usr-ratings/rate';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type RateProductInput = paths[url]['post']['parameters']['query'];

/**
 * Requests endpoint to rate a product.
 */
export const useRateProduct = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setCreatedProduct } = useData();

    const [loading, setLoading] = useState<boolean>(false);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    // general error message (for general errors, example: 500)
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // for errors associated with form fields (422 Unprocessable Content)
    const [validationErrors, setValidationErrors] = useState<any | null>(null);

    const request = async (props: RateProductInput) => {
        setLoading(true);
        setErrorMessage(null);
        setValidationErrors(null);
        setCreatedProduct(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }
                
                error.statusCode = response.status;
                
                throw error;
            }

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, errorMessage, validationErrors, lastResponseTime };
};
